import { format } from "date-fns";
import { useState } from "react";

import {
  CalendarDateRange,
  CalendarPicker,
  SearchableDropdown,
  SearchableDropdownItem,
} from "@fronterahealth/frontera-ui-components";

import { useBcbaSessionVideosAnalyticsQuery } from "@api/graphql/types-and-hooks";
import { CommentsAndFeedback } from "@pages/VideoAnalytics/BCBAs/CommentsAndFeedback";
import { EngagementMetrics } from "@pages/VideoAnalytics/BCBAs/EngagementMetrics";
import { GraphDialogs } from "@pages/VideoAnalytics/BCBAs/GraphDialogs";
import { DATE_FORMAT } from "@pages/VideoAnalytics/helpers";
import { useAdminData } from "@providers/AdminDataProvider";
import { useVideoAnalytics } from "@providers/VideoAnalyticsProvider";

export const BCBAMetrics = () => {
  const { isUserBCBA } = useAdminData();
  const { isAllBCBAFetching, allBCBAList, selectedBCBA, setSelectedBCBA, selectedDates, setSelectedDates } =
    useVideoAnalytics();

  const [isAvgVideosReviewedOpen, setIsAvgVideosReviewedOpen] = useState<boolean>(false);
  const [isPercentageVideosReviewedOpen, setIsPercentageVideosReviewedOpen] = useState<boolean>(false);

  const [isResponseTimeToRBTQuestionOpen, setIsResponseTimeToRBTQuestionOpen] = useState<boolean>(false);
  const [isResponseRateToRBTQuestionOpen, setIsResponseRateToRBTQuestionOpen] = useState<boolean>(false);

  const [isReviewCompletionDialogOpen, setIsReviewCompletionDialogOpen] = useState<boolean>(false);

  const [isCommentDistributionOpen, setIsCommentDistributionOpen] = useState<boolean>(false);
  const [isCommentOpen, setIsCommentOpen] = useState<boolean>(false);

  const [recentSearchesForBCBA, setRecentSearchesForBCBA] = useState<SearchableDropdownItem[]>([]);

  const handleDateChange = (newDates: CalendarDateRange) => {
    setSelectedDates(newDates);
  };

  const queryKey = [
    "bcbaSessionVideosAnalytics",
    selectedDates?.startDate?.toISOString() || "",
    selectedDates?.endDate?.toISOString() || "",
    selectedDates?.comparisonStartDate?.toISOString() || "",
    selectedDates?.comparisonEndDate?.toISOString() || "",
    selectedBCBA.map((item) => item.id).join(","),
  ];
  const { data: bcbaAnalyticsData, isFetching } = useBcbaSessionVideosAnalyticsQuery(
    {
      fromDate: selectedDates?.startDate
        ? format(selectedDates.startDate, DATE_FORMAT)
        : format(new Date(), DATE_FORMAT),
      toDate: selectedDates?.endDate ? format(selectedDates.endDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      comparisonFromDate: selectedDates?.comparisonStartDate
        ? format(selectedDates.comparisonStartDate, DATE_FORMAT)
        : undefined,
      comparisonToDate: selectedDates?.comparisonEndDate
        ? format(selectedDates.comparisonEndDate, DATE_FORMAT)
        : undefined,
      bcbaProviderIds: selectedBCBA.map((item) => item.id),
    },
    {
      queryKey,
      refetchOnWindowFocus: false,
      retry: false,
      enabled:
        Boolean(selectedDates?.startDate && selectedDates?.endDate) ||
        Boolean(selectedDates?.comparisonStartDate && selectedDates?.comparisonEndDate),
    },
  );

  const currentData = bcbaAnalyticsData?.bcbaSessionVideosAnalytics?.currentData;
  const previousPeriodData = bcbaAnalyticsData?.bcbaSessionVideosAnalytics?.comparisonData;

  const handleRecentSearchAddForBCBA = (item: SearchableDropdownItem) => {
    setRecentSearchesForBCBA((prev) => [...prev, item].slice(-5)); // Keep the last 5 searches
  };
  return (
    <div className="grid gap-4">
      <div className="flex flex-row items-center gap-2 justify-start">
        {!isUserBCBA ? (
          <SearchableDropdown
            items={allBCBAList}
            label="BCBA"
            placeholder="All BCBAs"
            onSelectionChange={(items: SearchableDropdownItem[]) => {
              setSelectedBCBA(items);
            }}
            recentSearches={recentSearchesForBCBA}
            onRecentSearchAdd={handleRecentSearchAddForBCBA}
            isLoading={isAllBCBAFetching}
            selectedItems={selectedBCBA}
            disabled={isAllBCBAFetching}
          />
        ) : null}
        <CalendarPicker
          comparisonEnabled
          placeholder="Date"
          selectedDateRange={selectedDates}
          inputSize="sm"
          onDateChange={handleDateChange}
          mode="filter"
          selectedLabel={"Last 7 Days"}
          selectedComparisonLabel="Previous Period"
        />
      </div>
      <EngagementMetrics
        currentData={currentData}
        previousPeriodData={previousPeriodData}
        isFetching={isFetching}
        setIsAvgVideosReviewedOpen={setIsAvgVideosReviewedOpen}
        setIsPercentageVideosReviewedOpen={setIsPercentageVideosReviewedOpen}
        setIsReviewCompletionDialogOpen={setIsReviewCompletionDialogOpen}
      />
      <CommentsAndFeedback
        currentData={currentData}
        previousPeriodData={previousPeriodData}
        isFetching={isFetching}
        setIsResponseTimeToRBTQuestionOpen={setIsResponseTimeToRBTQuestionOpen}
        setIsResponseRateToRBTQuestionOpen={setIsResponseRateToRBTQuestionOpen}
        setIsCommentDistributionOpen={setIsCommentDistributionOpen}
        setIsCommentOpen={setIsCommentOpen}
      />

      <GraphDialogs
        isAvgVideosReviewedOpen={isAvgVideosReviewedOpen}
        setIsAvgVideosReviewedOpen={setIsAvgVideosReviewedOpen}
        isPercentageVideosReviewedOpen={isPercentageVideosReviewedOpen}
        setIsPercentageVideosReviewedOpen={setIsPercentageVideosReviewedOpen}
        isResponseTimeToRBTQuestionOpen={isResponseTimeToRBTQuestionOpen}
        setIsResponseTimeToRBTQuestionOpen={setIsResponseTimeToRBTQuestionOpen}
        isResponseRateToRBTQuestionOpen={isResponseRateToRBTQuestionOpen}
        setIsResponseRateToRBTQuestionOpen={setIsResponseRateToRBTQuestionOpen}
        isCommentDistributionOpen={isCommentDistributionOpen}
        setIsCommentDistributionOpen={setIsCommentDistributionOpen}
        isCommentOpen={isCommentOpen}
        setIsCommentOpen={setIsCommentOpen}
        isReviewCompletionDialogOpen={isReviewCompletionDialogOpen}
        setIsReviewCompletionDialogOpen={setIsReviewCompletionDialogOpen}
      />
    </div>
  );
};
